import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Calendar,
  BarChart3,
  Clock,
  CheckCircle,
  XCircle,
  Edit2,
  Trash2,
  MoreVertical,
} from "lucide-react";
import { toast } from "react-toastify";
import pollServices from "../../../services/pollServices";
import folderService from "../../../services/folderServices";

const PollList = () => {
  const navigate = useNavigate();
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [openMenuId, setOpenMenuId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deletingPollId, setDeletingPollId] = useState(null);

  useEffect(() => {
    fetchPolls();
  }, []);

  const fetchPolls = async () => {
    setLoading(true);
    setError("");
    try {
      // Fetch polls from both the polls API and the elements/folders API
      const [pollsResponse, foldersResponse] = await Promise.all([
        pollServices.getPolls().catch(() => ({ data: [] })),
        folderService.getFolders().catch(() => ({ data: [] }))
      ]);

      // Extract polls from polls API
      let apiPolls = [];
      if (pollsResponse.success && pollsResponse.data) {
        apiPolls = Array.isArray(pollsResponse.data) ? pollsResponse.data : [];
      } else if (Array.isArray(pollsResponse)) {
        apiPolls = pollsResponse;
      } else if (pollsResponse.data && Array.isArray(pollsResponse.data)) {
        apiPolls = pollsResponse.data;
      }

      // Extract poll elements from folders
      let elementPolls = [];
      const folders = foldersResponse.success ? foldersResponse.data : (foldersResponse.data || foldersResponse || []);
      if (Array.isArray(folders)) {
        folders.forEach(folder => {
          if (folder.elements && Array.isArray(folder.elements)) {
            folder.elements.forEach(element => {
              if (element.type === 'poll') {
                // Convert element poll to same format as API polls
                elementPolls.push({
                  _id: element._id,
                  question: element.question || element.name,
                  options: element.options || [],
                  status: element.status || 'draft',
                  createdAt: element.createdAt,
                  updatedAt: element.updatedAt,
                  settings: {
                    shareResultsLive: element.results_settings?.show_immediately ?? true,
                    showResultsAtEnd: element.results_settings?.show_after_voting ?? true,
                    allowMultipleAnswers: element.poll_type === 'multiple_answer',
                    anonymousVoting: element.results_settings?.anonymous_voting ?? false,
                    autoCloseAfter: element.duration?.always_visible === false,
                    autoCloseSeconds: element.duration?.seconds || 60,
                  },
                  // Keep original element data for reference
                  _isElementPoll: true,
                  _elementData: element,
                  poll_type: element.poll_type,
                  appearance: element.appearance,
                  results_settings: element.results_settings,
                  duration: element.duration,
                });
              }
            });
          }
        });
      }

      // Merge polls, avoiding duplicates (by pollId reference or matching question)
      const apiPollIds = new Set(apiPolls.map(p => p._id || p.id));
      const mergedPolls = [
        ...apiPolls,
        ...elementPolls.filter(ep => {
          // Don't add if it has a pollId that matches an API poll
          if (ep._elementData?.pollId && apiPollIds.has(ep._elementData.pollId)) {
            return false;
          }
          // Don't add if there's an API poll with the same ID
          if (apiPollIds.has(ep._id)) {
            return false;
          }
          return true;
        })
      ];

      // Sort by createdAt date, newest first
      mergedPolls.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA;
      });

      setPolls(mergedPolls);
    } catch (err) {
      console.error("Error fetching polls:", err);
      setError(err.message || "Error fetching polls");
      setPolls([]);
      toast.error(err.message || "Failed to fetch polls");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (err) {
      return dateString;
    }
  };

  const getStatusBadge = (status) => {
    const statusLower = status?.toLowerCase() || "inactive";
    if (statusLower === "active") {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
          <CheckCircle className="h-3 w-3 mr-1" />
          Active
        </span>
      );
    } else if (statusLower === "closed") {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30">
          <XCircle className="h-3 w-3 mr-1" />
          Closed
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-500/20 text-gray-400 border border-gray-500/30">
          <Clock className="h-3 w-3 mr-1" />
          {status || "Inactive"}
        </span>
      );
    }
  };

  const handleEditPoll = (poll, e) => {
    e.stopPropagation();
    setOpenMenuId(null);
    navigate(`/dashboard/edit-poll/${poll._id || poll.id}`, {
      state: { poll },
    });
  };

  const handleDeletePoll = async (pollId, e) => {
    e.stopPropagation();
    setOpenMenuId(null);
    setDeleteConfirmId(pollId);
  };

  const confirmDelete = async (pollId, e) => {
    e.stopPropagation();
    setDeletingPollId(pollId);
    try {
      // Find the poll to check if it's an element poll
      const pollToDelete = polls.find(p => (p._id || p.id) === pollId);

      if (pollToDelete?._isElementPoll) {
        // Delete from elements system
        await folderService.deleteElement(pollId);
        toast.success("Poll deleted successfully!");
        setPolls(polls.filter((poll) => (poll._id || poll.id) !== pollId));
      } else {
        // Delete from polls API
        const response = await pollServices.deletePoll(pollId);
        if (response.success) {
          toast.success("Poll deleted successfully!");
          setPolls(polls.filter((poll) => (poll._id || poll.id) !== pollId));
        } else {
          toast.error(response.message || "Failed to delete poll");
        }
      }
    } catch (error) {
      console.error("Error deleting poll:", error);
      toast.error(error.message || "Failed to delete poll");
    } finally {
      setDeleteConfirmId(null);
      setDeletingPollId(null);
    }
  };

  const cancelDelete = (e) => {
    e.stopPropagation();
    setDeleteConfirmId(null);
  };

  const toggleMenu = (pollId, e) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === pollId ? null : pollId);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenMenuId(null);
    if (openMenuId) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [openMenuId]);

  if (loading) {
    return (
      <div className="min-h-screen text-white p-3 md:p-6 flex items-center justify-center">
        <div className="text-base md:text-xl">Loading polls...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white p-3 md:p-6">
      <div className="max-w-6xl mx-auto space-y-4 md:space-y-8">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row justify-between md:items-center gap-3 md:gap-0">
          <h1 className="text-xl md:text-3xl font-bold text-white uppercase">
            Polls
          </h1>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
            <button
              onClick={() => navigate("/dashboard/create-poll")}
              className="px-3 md:px-4 py-2 bg-[#7700FF] text-white rounded-lg transition-colors font-semibold hover:bg-[#6600DD] flex items-center justify-center space-x-2 text-sm md:text-base"
            >
              <Plus className="h-4 w-4" />
              <span>Create Poll</span>
            </button>
            <div className="text-xs md:text-sm text-gray-300">
              Total: {polls.length} {polls.length === 1 ? "poll" : "polls"}
            </div>
          </div>
        </div>

        {/* Polls List */}
        <div className="rounded-xl border border-gray-600 overflow-hidden primary-card-background">
          <div className="px-4 md:px-6 py-3 md:py-4 border-b border-gray-700">
            <h2 className="text-base md:text-lg font-semibold text-white uppercase">
              ALL POLLS
            </h2>
          </div>

          <div className="p-3 md:p-6">
            {error && (
              <div className="text-red-400 text-center py-4 text-sm md:text-base">
                {error}
              </div>
            )}

            {polls.length === 0 && !loading && !error && (
              <div className="text-center py-12">
                <BarChart3 className="h-16 w-16 text-gray-500 mx-auto mb-4" />
                <p className="text-gray-400 text-base md:text-lg mb-4">
                  No polls found
                </p>
                <button
                  onClick={() => navigate("/dashboard/create-poll")}
                  className="px-4 py-2 bg-[#7700FF] text-white rounded-lg transition-colors font-semibold hover:bg-[#6600DD] flex items-center justify-center space-x-2 mx-auto"
                >
                  <Plus className="h-4 w-4" />
                  <span>Create Your First Poll</span>
                </button>
              </div>
            )}

            <div className="space-y-4">
              {polls.map((poll) => {
                const pollId = poll._id || poll.id;
                return (
                  <div
                    key={pollId}
                    className="p-4 md:p-6 bg-gray-800/50 rounded-lg border border-gray-700 hover:border-gray-600 transition-colors cursor-pointer relative"
                    onClick={() => {
                      // Navigate to poll details or results view
                      console.log("Poll clicked:", poll);
                    }}
                  >
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4 mb-3">
                          <h3 className="text-white font-semibold text-base md:text-lg flex-1">
                            {poll.question || "Untitled Poll"}
                          </h3>
                          <div className="flex items-center gap-2">
                            {getStatusBadge(poll.status)}

                            {/* Actions Menu */}
                            <div className="relative">
                              <button
                                onClick={(e) => toggleMenu(pollId, e)}
                                className="p-1 hover:bg-gray-700 rounded transition-colors"
                                title="More options"
                              >
                                <MoreVertical className="h-5 w-5 text-gray-400" />
                              </button>

                              {openMenuId === pollId && (
                                <div className="absolute right-0 mt-2 w-48 bg-gray-800 rounded-lg shadow-lg border border-gray-700 z-10">
                                  <button
                                    onClick={(e) => handleEditPoll(poll, e)}
                                    className="w-full px-4 py-2 text-left text-sm text-white hover:bg-gray-700 flex items-center gap-2 rounded-t-lg transition-colors"
                                  >
                                    <Edit2 className="h-4 w-4" />
                                    Edit Poll
                                  </button>
                                  <button
                                    onClick={(e) => handleDeletePoll(pollId, e)}
                                    className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-gray-700 flex items-center gap-2 rounded-b-lg transition-colors"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                    Delete Poll
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="space-y-2 mb-4">
                          <div className="flex items-center text-gray-400 text-sm">
                            <BarChart3 className="h-4 w-4 mr-2" />
                            <span>
                              {poll.options?.length || 0} option
                              {(poll.options?.length || 0) !== 1 ? "s" : ""}
                            </span>
                          </div>

                          {poll.createdAt && (
                            <div className="flex items-center text-gray-400 text-sm">
                              <Calendar className="h-4 w-4 mr-2" />
                              <span>Created: {formatDate(poll.createdAt)}</span>
                            </div>
                          )}

                          {poll.settings && (
                            <div className="flex flex-wrap gap-2 mt-3">
                              {poll.settings.allowMultipleAnswers && (
                                <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded border border-blue-500/30">
                                  Multiple Answers
                                </span>
                              )}
                              {poll.settings.anonymousVoting && (
                                <span className="px-2 py-1 bg-purple-500/20 text-purple-400 text-xs rounded border border-purple-500/30">
                                  Anonymous
                                </span>
                              )}
                              {poll.settings.shareResultsLive && (
                                <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded border border-green-500/30">
                                  Live Results
                                </span>
                              )}
                              {poll.settings.autoCloseAfter && (
                                <span className="px-2 py-1 bg-orange-500/20 text-orange-400 text-xs rounded border border-orange-500/30">
                                  Auto-close: {poll.settings.autoCloseSeconds}s
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Show options preview */}
                        {poll.options && poll.options.length > 0 && (
                          <div className="mt-3">
                            <p className="text-gray-500 text-xs mb-2">
                              Options:
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {poll.options.slice(0, 3).map((option, index) => (
                                <span
                                  key={index}
                                  className="px-2 py-1 bg-gray-700/50 text-gray-300 text-xs rounded border border-gray-600"
                                >
                                  {option.text}
                                </span>
                              ))}
                              {poll.options.length > 3 && (
                                <span className="px-2 py-1 bg-gray-700/50 text-gray-400 text-xs rounded border border-gray-600">
                                  +{poll.options.length - 3} more
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Delete Confirmation Modal */}
                    {deleteConfirmId === pollId && (
                      <div
                        className="absolute inset-0 bg-black/60 rounded-lg flex items-center justify-center z-20"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="bg-gray-800 p-6 rounded-lg border border-gray-700 max-w-sm mx-4">
                          <h4 className="text-white font-semibold text-lg mb-2">
                            Delete Poll?
                          </h4>
                          <p className="text-gray-400 text-sm mb-4">
                            Are you sure you want to delete this poll? This
                            action cannot be undone.
                          </p>
                          <div className="flex gap-3 justify-end">
                            <button
                              onClick={cancelDelete}
                              disabled={deletingPollId === pollId}
                              className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={(e) => confirmDelete(pollId, e)}
                              disabled={deletingPollId === pollId}
                              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                              {deletingPollId === pollId ? (
                                <>
                                  <svg
                                    className="animate-spin h-4 w-4"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                    ></circle>
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                  </svg>
                                  Deleting...
                                </>
                              ) : (
                                "Delete"
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PollList;
